import { useEffect, useState } from 'react';
import { Trash2, RefreshCw } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Input } from '@/components/ui/Input';
import { formatBytes } from '@/lib/utils';
import { toast } from 'sonner';

interface Entry {
  key: string;
  value: string;
  size: number;
}

function readStorage(s: Storage): Entry[] {
  const out: Entry[] = [];
  for (let i = 0; i < s.length; i++) {
    const k = s.key(i)!;
    const v = s.getItem(k) ?? '';
    out.push({ key: k, value: v, size: k.length + v.length });
  }
  return out.sort((a, b) => b.size - a.size);
}

export function StorageInspector() {
  const [tab, setTab] = useState<'local' | 'session'>('local');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [filter, setFilter] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setEntries(readStorage(tab === 'local' ? localStorage : sessionStorage));
  }, [tab, tick]);

  const filtered = entries.filter((e) =>
    !filter || e.key.toLowerCase().includes(filter.toLowerCase()),
  );

  const totalSize = entries.reduce((s, e) => s + e.size, 0) * 2; // UTF-16

  function remove(k: string) {
    (tab === 'local' ? localStorage : sessionStorage).removeItem(k);
    setTick((t) => t + 1);
    toast.success('Removed');
  }

  function clearAll() {
    if (!confirm(`Clear all ${tab}Storage entries for this site?`)) return;
    (tab === 'local' ? localStorage : sessionStorage).clear();
    setTick((t) => t + 1);
    toast.success('Cleared');
  }

  return (
    <div>
      <ToolHeader
        toolId="storage-inspector"
        title="Browser storage inspector"
        description="Read, search, and clear localStorage and sessionStorage for this origin."
      />

      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList>
            <TabsTrigger value="local">localStorage</TabsTrigger>
            <TabsTrigger value="session">sessionStorage</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} />
        </Tabs>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter keys…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="!h-8 max-w-xs"
          />
          <Button size="sm" variant="ghost" onClick={() => setTick((t) => t + 1)}>
            <RefreshCw size={11} />
          </Button>
          <Button size="sm" variant="danger" onClick={clearAll} disabled={!entries.length}>
            <Trash2 size={11} />
            Clear all
          </Button>
        </div>
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between text-xs">
          <span className="font-medium text-fg-muted">{filtered.length} entries</span>
          <span className="font-mono text-fg-subtle">{formatBytes(totalSize)} total</span>
        </div>
        {filtered.length === 0 ? (
          <div className="p-6 text-sm text-fg-subtle text-center">No entries</div>
        ) : (
          <div className="divide-y divide-border max-h-[520px] overflow-y-auto">
            {filtered.map((e) => (
              <div key={e.key} className="p-3 group">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <code className="text-sm font-mono font-medium truncate">{e.key}</code>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-2xs font-mono text-fg-subtle">
                      {formatBytes(e.size * 2)}
                    </span>
                    <button
                      onClick={() => remove(e.key)}
                      className="opacity-0 group-hover:opacity-100 transition size-7 rounded text-fg-subtle hover:text-danger hover:bg-surface-hover flex items-center justify-center"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-fg-muted font-mono break-all line-clamp-2">
                  {e.value || <span className="italic">empty</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default StorageInspector;
